import { useEffect, useRef, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";
import { Trash2, UserPlus, ShieldAlert, X, Lock, Stamp, Power, QrCode, AlertTriangle, KeyRound, Building2, Image as ImageIcon, Upload, RotateCcw, PenLine, FileText } from "lucide-react";
import { Navigate } from "react-router-dom";
import ResponsiveTable from "../components/ResponsiveTable";
import PasswordInput from "../components/PasswordInput";

export default function SuperAdmin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [cleanOpen, setCleanOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetRevealed, setResetRevealed] = useState(false);  // password-gated visibility
  const [revealOpen, setRevealOpen] = useState(false);  // password modal to unlock
  const [cleaning, setCleaning] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetPwUser, setResetPwUser] = useState(null);  // user being password-reset by admin

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load users");
    }
  };
  useEffect(() => { loadUsers(); }, []);

  if (user === null) return null; // still loading
  if (!user?.is_super_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  const cleanTestData = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/auth/admin/clean-test-data");
      toast.success(`Removed ${data.total} test record${data.total === 1 ? "" : "s"}`);
      setCleanOpen(false);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  const resetAllData = async (password) => {
    setResetting(true);
    try {
      const { data } = await api.post("/auth/admin/reset-all-data", { password });
      toast.success(`Factory reset — ${data.total} record${data.total === 1 ? "" : "s"} wiped`);
      setResetOpen(false);
      setResetRevealed(false);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  // Unlock the hidden card by re-typing the super admin password (verified server-side at action time).
  const handleReveal = (password) => {
    // Cheap client gate — server still re-verifies on the actual wipe call.
    if (!password || password.length < 1) {
      toast.error("Password required");
      return false;
    }
    setResetRevealed(true);
    setRevealOpen(false);
    toast.info("Factory reset unlocked for this session");
    return true;
  };

  const removeUser = async (u) => {
    if (u.is_super_admin) return toast.error("Cannot delete super admin");
    if (u.id === user.id) return toast.error("Cannot delete yourself");
    if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const toggleActive = async (u) => {
    if (u.is_super_admin) return toast.error("Super admin cannot be deactivated");
    if (u.id === user.id) return toast.error("You cannot deactivate yourself");
    const next = !u.is_active;
    const verb = next ? "Reactivate" : "Deactivate";
    if (!window.confirm(`${verb} ${u.email}?${next ? "" : "\n\nThey will be signed out immediately and blocked from logging in until reactivated."}`)) return;
    try {
      await api.patch(`/auth/users/${u.id}/active`, { is_active: next });
      toast.success(`${u.email} · ${next ? "Active" : "Inactive"}`);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
  };

  const columns = [
    { key: "name", label: "Name", width: "22%", cell: (u) => (
      <div className="min-w-0">
        <div className="truncate">{u.name}</div>
        <div className="text-[0.65rem] mono text-[#666] mt-0.5 truncate">{u.email}</div>
      </div>
    ) },
    { key: "role", label: "Role", width: "130px", cell: (u) => (
      <span className={`status-badge ${u.role === "admin" ? "status-available" : "status-reserved"}`}>
        {u.role}{u.is_super_admin ? " · SUPER" : ""}
      </span>
    ) },
    { key: "warehouse", label: "Warehouse", width: "140px", cell: (u) => (
      <span className="text-[0.7rem] mono text-[#8A8A8A]">{u.default_warehouse || "—"}</span>
    ), hideOnMobile: true },
    { key: "status", label: "Status", width: "110px", cell: (u) => (
      u.is_active === false ? (
        <span
          className="px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] border border-red-500/40 text-red-400 bg-red-500/[0.05] inline-block"
          data-testid={`user-status-inactive-${u.id}`}
        >
          Inactive
        </span>
      ) : (
        <span
          className="px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] border border-[#7BA585]/40 text-[#7BA585] bg-[#7BA585]/[0.05] inline-block"
          data-testid={`user-status-active-${u.id}`}
        >
          Active
        </span>
      )
    ) },
    { key: "created", label: "Created", width: "120px", cell: (u) => (
      <span className="mono text-xs text-[#666]">{(u.created_at || "").slice(0, 10)}</span>
    ), hideOnMobile: true },
    { key: "commission", label: "Commission", width: "130px", cell: (u) => (
      <CommissionCell user={u} onSaved={loadUsers} />
    ), hideOnMobile: true },
  ];

  const rowActions = (u) => (
    <>
      <button
        data-testid={`reset-password-${u.id}`}
        onClick={() => setResetPwUser(u)}
        disabled={u.is_super_admin && u.id !== user.id}
        className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-[#D4AF37] disabled:opacity-30 disabled:hover:text-[#8A8A8A] inline-flex items-center justify-center"
        aria-label="Reset password"
        title={u.is_super_admin && u.id !== user.id ? "Only the super admin can reset their own password" : "Reset password (admin override)"}
      >
        <KeyRound className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <button
        data-testid={`toggle-active-${u.id}`}
        onClick={() => toggleActive(u)}
        disabled={u.is_super_admin || u.id === user.id}
        className={`min-h-[44px] sm:min-h-0 p-2 disabled:opacity-30 disabled:hover:text-[#8A8A8A] inline-flex items-center justify-center transition-colors ${
          u.is_active === false
            ? "text-red-400 hover:text-[#7BA585]"
            : "text-[#7BA585] hover:text-red-400"
        }`}
        aria-label={u.is_active === false ? "Reactivate user" : "Deactivate user"}
        title={
          u.is_super_admin
            ? "Super admin protected"
            : u.id === user.id
            ? "You cannot deactivate yourself"
            : u.is_active === false
            ? "Reactivate (allow login)"
            : "Deactivate (block login + force logout)"
        }
      >
        <Power className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <button
        data-testid={`delete-user-${u.id}`}
        onClick={() => removeUser(u)}
        disabled={u.is_super_admin || u.id === user.id}
        className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-red-400 disabled:opacity-30 disabled:hover:text-[#8A8A8A] inline-flex items-center justify-center"
        aria-label="Delete user"
        title={u.is_super_admin ? "Super admin protected" : u.id === user.id ? "Cannot delete yourself" : "Delete user"}
      >
        <Trash2 className="w-4 h-4" strokeWidth={1.25} />
      </button>
    </>
  );

  return (
    <Layout title="Super Admin" subtitle="Privileged controls">
      <Toaster theme="dark" position="top-right" />

      {/* Company info (rendered on every PDF) */}
      <CompanyInfoCard />

      {/* Company logo (rendered on every PDF) */}
      <CompanyLogoCard />

      {/* e-Meterai threshold card */}
      <EmeteraiThresholdCard />

      {/* Memo warehouse whitelist — decides which warehouses may source memos */}
      <MemoWarehouseCard />

      {/* Warranty card terms — Super Admin edits the paragraph that appears
          on every gemstone warranty card PDF. */}
      <WarrantyCardTermsCard />

      {/* Top action cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        <div className="glass-card p-6 sm:p-7">
          <div className="flex items-start gap-4">
            <div className="p-3 border border-[#D4AF37]/30 bg-[#D4AF37]/[0.05]">
              <UserPlus className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.25} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline">User Management</div>
              <h3 className="serif text-xl mt-1">Invite Internal User</h3>
              <p className="text-xs text-[#8A8A8A] mt-2 mb-4">
                Create admin, accountant, staff, or sales logins. New admins do NOT get super-admin powers.
              </p>
              <button
                onClick={() => setInviteOpen(true)}
                data-testid="invite-user-btn"
                className="btn-gold min-h-[44px] px-5 py-2 inline-flex items-center gap-2 text-xs"
              >
                <UserPlus className="w-3.5 h-3.5" /> Invite New User
              </button>
            </div>
          </div>
        </div>
        <div className="glass-card p-6 sm:p-7 border-red-400/20">
          <div className="flex items-start gap-4">
            <div className="p-3 border border-red-400/30 bg-red-400/[0.05]">
              <ShieldAlert className="w-5 h-5 text-red-400" strokeWidth={1.25} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline text-red-400/80">Danger Zone</div>
              <h3 className="serif text-xl mt-1">Clean Test Data</h3>
              <p className="text-xs text-[#8A8A8A] mt-2 mb-4">
                Permanently deletes records whose name/SKU starts with{" "}
                <span className="mono text-[#D4AF37]">TEST_</span> or{" "}
                <span className="mono text-[#D4AF37]">TESTMULTI_</span>.
              </p>
              <button
                onClick={() => setCleanOpen(true)}
                data-testid="clean-test-data-btn"
                className="min-h-[44px] px-5 py-2 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" /> Clean Test Data
              </button>
            </div>
          </div>
        </div>
        {resetRevealed ? (
          <div className="glass-card p-6 sm:p-7 border-red-400/40">
            <div className="flex items-start gap-4">
              <div className="p-3 border border-red-400/50 bg-red-400/[0.08]">
                <ShieldAlert className="w-5 h-5 text-red-400" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="overline text-red-400/90">CRITICAL — Factory Reset</div>
                <h3 className="serif text-xl mt-1">Wipe All Data</h3>
                <p className="text-xs text-[#8A8A8A] mt-2 mb-4">
                  Permanently deletes <strong className="text-red-400">EVERY</strong> product,
                  customer, supplier, sale, expense, auction, commission, stock movement,
                  stock transfer, sales user, and audit log. Internal users are preserved.
                  Cannot be undone.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setResetOpen(true)}
                    data-testid="reset-all-data-btn"
                    className="min-h-[44px] px-5 py-2 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] border-2 border-red-400 text-red-400 hover:bg-red-400/15 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Wipe All Data
                  </button>
                  <button
                    onClick={() => setResetRevealed(false)}
                    className="min-h-[44px] px-4 py-2 text-[0.65rem] uppercase tracking-wider text-[#8A8A8A] hover:text-[#D4AF37]"
                    data-testid="reset-relock-btn"
                  >
                    Lock again
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setRevealOpen(true)}
            data-testid="reset-reveal-btn"
            className="glass-card p-6 sm:p-7 border-white/5 text-left hover:border-red-400/30 transition-colors group"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 border border-white/10 bg-white/[0.02] group-hover:border-red-400/30">
                <Lock className="w-5 h-5 text-[#8A8A8A] group-hover:text-red-400" strokeWidth={1.25} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="overline text-[#666]">Password-Protected</div>
                <h3 className="serif text-xl mt-1 text-[#8A8A8A] group-hover:text-[#EAEAEA]">Factory Reset</h3>
                <p className="text-xs text-[#666] mt-2">
                  Hidden by default. Click to unlock with your account password.
                </p>
              </div>
            </div>
          </button>
        )}
      </div>

      <div className="overline mb-3">Internal Users ({users.length})</div>
      <UsersTable users={users} columns={columns} rowActions={rowActions} />

      {inviteOpen && (
        <InviteUserModal onClose={() => setInviteOpen(false)} onSaved={() => { setInviteOpen(false); loadUsers(); }} />
      )}
      {cleanOpen && (
        <ConfirmModal
          title="Clean Test Data?"
          subtitle="Permanent · cannot undo"
          body={
            <>
              <p className="text-sm text-[#8A8A8A] mb-3">
                This will permanently delete every record across products, customers,
                suppliers, sales, expenses, auctions, commissions, stock movements,
                stock transfers and internal users whose name / SKU / email begins with{" "}
                <span className="mono text-[#D4AF37]">TEST_</span> or{" "}
                <span className="mono text-[#D4AF37]">TESTMULTI_</span>.
              </p>
              <p className="text-xs text-red-400 mono">This action cannot be undone.</p>
            </>
          }
          confirmLabel={cleaning ? "Cleaning…" : "Yes, delete all test data"}
          confirmDisabled={cleaning}
          confirmDanger
          onConfirm={cleanTestData}
          onClose={() => setCleanOpen(false)}
        />
      )}
      {resetOpen && (
        <ResetConfirmModal
          resetting={resetting}
          onConfirm={resetAllData}
          onClose={() => setResetOpen(false)}
        />
      )}
      {revealOpen && (
        <RevealResetModal
          onUnlock={handleReveal}
          onClose={() => setRevealOpen(false)}
        />
      )}
      {resetPwUser && (
        <AdminResetPasswordModal
          target={resetPwUser}
          onClose={() => setResetPwUser(null)}
          onSaved={() => setResetPwUser(null)}
        />
      )}
    </Layout>
  );
}

/**
 * Inline-edit commission rate cell. Click the value → input opens; Enter/blur
 * saves; Escape cancels. Only renders an editable value for sales-role users —
 * non-sales rows show a quiet dash because commission doesn't apply.
 */
function CommissionCell({ user: u, onSaved }) {
  const isSales = u.role === "sales";
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(u.commission_rate ?? 0);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setVal(u.commission_rate ?? 0); }, [u.commission_rate]);
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  if (!isSales) {
    return <span className="text-[0.7rem] text-[#444]" data-testid={`commission-na-${u.id}`}>—</span>;
  }

  const save = async () => {
    const rate = parseFloat(val);
    if (Number.isNaN(rate) || rate < 0 || rate > 100) {
      toast.error("Rate must be between 0 and 100");
      setVal(u.commission_rate ?? 0);
      setEditing(false);
      return;
    }
    // No-op if unchanged — skip the API call
    if (Math.abs(rate - (u.commission_rate ?? 0)) < 0.001) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/auth/users/${u.id}/commission`, { commission_rate: rate });
      toast.success(`${u.email} · ${rate}%`);
      setEditing(false);
      await onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
      setVal(u.commission_rate ?? 0);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1" data-testid={`commission-edit-${u.id}`}>
        <input
          ref={inputRef}
          type="number"
          step="0.1"
          min="0"
          max="100"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            else if (e.key === "Escape") { setVal(u.commission_rate ?? 0); setEditing(false); }
          }}
          disabled={saving}
          className="luxe-input w-16 px-2 py-1 text-xs mono"
          data-testid={`commission-input-${u.id}`}
        />
        <span className="text-xs text-[#8A8A8A]">%</span>
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      data-testid={`commission-display-${u.id}`}
      className="text-xs mono text-[#D4AF37] hover:text-[#F3E5AB] border-b border-dashed border-transparent hover:border-[#D4AF37]/40 px-1 py-0.5 transition-colors"
      title="Click to edit commission rate"
    >
      {u.commission_rate ?? 0}%
    </button>
  );
}



function UsersTable({ users, columns, rowActions }) {
  return (
    <ResponsiveTable
      columns={columns}
      rows={users}
      rowKey={(u) => u.id}
      testid="users-table"
      rowActions={rowActions}
      emptyMessage="No users yet."
      mobileCard={(u) => ({
        title: <span>{u.name}</span>,
        subtitle: <span className="mono text-[#8A8A8A]">{u.email}</span>,
        fields: [
          { label: "Role", value: (
            <span className={`status-badge ${u.role === "admin" ? "status-available" : "status-reserved"}`}>
              {u.role}{u.is_super_admin ? " · SUPER" : ""}
            </span>
          ) },
          { label: "Warehouse", value: <span className="mono">{u.default_warehouse || "—"}</span> },
          { label: "Created", value: <span className="mono">{(u.created_at || "").slice(0, 10) || "—"}</span> },
        ],
        actions: rowActions(u),
      })}
    />
  );
}

function InviteUserModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "staff",
    default_warehouse: "ONLINE_SALE",
    commission_rate: 10,
    monthly_target: 0,
  });
  const [submitting, setSubmitting] = useState(false);

  // When switching TO admin role, default to "ALL" (unscoped) so the existing
  // admin invite flow stays backward-compatible.
  const onRoleChange = (nextRole) => {
    setForm((prev) => ({
      ...prev,
      role: nextRole,
      default_warehouse:
        nextRole === "admin"
          ? "ALL"
          : (prev.default_warehouse === "ALL" ? "ONLINE_SALE" : prev.default_warehouse),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
        // Admin: warehouse is optional. `null` (or the "ALL" sentinel from the
        // dropdown) means "unscoped admin" — sees every warehouse. Assigning
        // a specific warehouse creates a warehouse-scoped admin (1 per WH).
        default_warehouse:
          form.role === "admin"
            ? (form.default_warehouse === "ALL" ? null : form.default_warehouse)
            : form.default_warehouse,
      };
      if (form.role === "sales") {
        payload.commission_rate = parseFloat(form.commission_rate) || 0;
        payload.monthly_target = parseFloat(form.monthly_target) || 0;
      }
      await api.post("/auth/users/invite", payload);
      toast.success(
        form.role === "sales"
          ? `Invited ${form.email} · ${form.commission_rate}% commission`
          : `Invited ${form.email}`,
      );
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invite failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#0F0F0F] border border-white/10 w-full max-w-md max-h-[92vh] overflow-y-auto fade-up">
        <div className="sticky top-0 z-10 bg-[#0F0F0F] border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">New Login</div>
            <h3 className="serif text-2xl mt-1">Invite User</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        <form onSubmit={submit} className="px-7 py-6 space-y-4" data-testid="invite-form">
          <Field label="Full Name">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="invite-name" />
          </Field>
          <Field label="Email">
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm mono" data-testid="invite-email" />
          </Field>
          <Field label="Password (min 6)">
            <PasswordInput required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm mono" data-testid="invite-password" />
          </Field>
          <Field label="Role">
            <select value={form.role} onChange={(e) => onRoleChange(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="invite-role">
              <option value="admin">Admin (manage everything, no super powers)</option>
              <option value="accountant">Accountant (read financials, no delete)</option>
              <option value="staff">Staff (inventory + sales)</option>
              <option value="sales">Sales (own customers + commission)</option>
            </select>
          </Field>
          <Field
            label={
              form.role === "admin"
                ? "Warehouse Assignment (1 admin per warehouse)"
                : "Default Warehouse"
            }
          >
            <select
              value={form.default_warehouse}
              onChange={(e) => setForm({ ...form, default_warehouse: e.target.value })}
              className="luxe-input w-full px-4 py-2.5 text-sm"
              data-testid="invite-warehouse"
            >
              {form.role === "admin" && (
                <option value="ALL">All Warehouses (unscoped)</option>
              )}
              <option value="ONLINE_SALE">Online Sale</option>
              <option value="SHOWROOM">Showroom</option>
              <option value="LIVE_PRODUCT">Live Product</option>
            </select>
            {form.role === "admin" && form.default_warehouse !== "ALL" && (
              <div className="mt-1.5 text-[0.65rem] mono text-[#C99356]" data-testid="admin-scope-hint">
                This admin will only see inventory + sales + dashboard KPIs for {form.default_warehouse.replace("_", " ")}.
              </div>
            )}
          </Field>
          {form.role === "sales" && (
            <div className="grid grid-cols-2 gap-3 border border-[#D4AF37]/15 p-4 bg-[#D4AF37]/[0.025]" data-testid="invite-commission-fields">
              <Field label="Commission Rate (%)">
                <input
                  type="number" step="0.1" min="0" max="100"
                  value={form.commission_rate}
                  onChange={(e) => setForm({ ...form, commission_rate: e.target.value })}
                  className="luxe-input w-full px-4 py-2.5 text-sm mono"
                  data-testid="invite-commission-rate"
                />
              </Field>
              <Field label="Monthly Target (IDR)">
                <input
                  type="number" step="1" min="0"
                  value={form.monthly_target}
                  onChange={(e) => setForm({ ...form, monthly_target: e.target.value })}
                  placeholder="0 = no target"
                  className="luxe-input w-full px-4 py-2.5 text-sm mono"
                  data-testid="invite-monthly-target"
                />
              </Field>
              <div className="col-span-2 text-[0.65rem] text-[#8A8A8A]">
                A sales profile will be auto-created and linked to this login. Commission rates can be adjusted per-sale at record time.
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-gold min-h-[44px] px-5 py-2.5">Cancel</button>
            <button type="submit" disabled={submitting} className="btn-gold min-h-[44px] px-5 py-2.5" data-testid="invite-submit">
              {submitting ? "Creating…" : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RevealResetModal({ onUnlock, onClose }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    onUnlock(password);
    setSubmitting(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#0F0F0F] border border-white/10 w-full max-w-sm fade-up">
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">Password-Protected</div>
            <h3 className="serif text-2xl mt-1">Unlock Factory Reset</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        <form onSubmit={submit} className="px-7 py-6 space-y-4" data-testid="reveal-reset-form">
          <p className="text-xs text-[#8A8A8A]">
            Enter your super-admin password to reveal the factory reset control.
            The server will verify it again when you actually run the wipe.
          </p>
          <Field label="Your account password">
            <PasswordInput
              autoFocus
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="luxe-input w-full px-4 py-2.5 text-sm mono"
              data-testid="reveal-reset-password"
            />
          </Field>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-outline-gold min-h-[44px] px-5 py-2.5">Cancel</button>
            <button
              type="submit"
              disabled={submitting || !password}
              data-testid="reveal-reset-submit"
              className="btn-gold min-h-[44px] px-5 py-2.5"
            >
              Unlock
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetConfirmModal({ resetting, onConfirm, onClose }) {
  const [typed, setTyped] = useState("");
  const [password, setPassword] = useState("");
  const armed = typed.trim().toUpperCase() === "WIPE" && password.length > 0;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#0F0F0F] border-2 border-red-400/60 w-full max-w-md fade-up">
        <div className="border-b border-red-400/20 px-7 py-5 flex justify-between items-center bg-red-400/[0.05]">
          <div>
            <div className="overline text-red-400/90">CRITICAL · cannot undo</div>
            <h3 className="serif text-2xl mt-1 text-red-400">Wipe All Data?</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        <div className="px-7 py-6">
          <p className="text-sm text-[#EAEAEA] mb-4">
            You are about to permanently delete <strong className="text-red-400">EVERY</strong>{" "}
            product, customer, supplier, sale, expense, auction, commission,
            stock movement, stock transfer, sales user, audit log, and uploaded file.
          </p>
          <p className="text-sm text-[#8A8A8A] mb-5">
            Internal user logins (super admin + admins/staff/sales) will be preserved.
            The SKU counter resets so your next product will be{" "}
            <span className="mono text-[#D4AF37]">GV-00001</span>.
          </p>
          <label className="overline block mb-2 text-red-400">Type <span className="mono">WIPE</span> to confirm</label>
          <input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="WIPE"
            className="luxe-input w-full px-4 py-2.5 text-sm mono mb-4"
            data-testid="reset-confirm-input"
          />
          <label className="overline block mb-2 text-red-400">Re-enter your password</label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Account password"
            className="luxe-input w-full px-4 py-2.5 text-sm mono"
            data-testid="reset-confirm-password"
          />
          <div className="flex justify-end gap-2 pt-5 mt-5 border-t border-red-400/15">
            <button type="button" onClick={onClose} className="btn-outline-gold min-h-[44px] px-5 py-2.5">Cancel</button>
            <button
              type="button"
              onClick={() => onConfirm(password)}
              disabled={!armed || resetting}
              data-testid="confirm-reset-all-data"
              className="min-h-[44px] px-5 py-2.5 uppercase tracking-[0.18em] text-xs border-2 border-red-400 text-red-400 hover:bg-red-400/15 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
            >
              {resetting ? "Wiping…" : "Wipe All Data Now"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, subtitle, body, confirmLabel, confirmDisabled, confirmDanger, onConfirm, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#0F0F0F] border border-white/10 w-full max-w-md fade-up">
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline text-red-400/80">{subtitle}</div>
            <h3 className="serif text-2xl mt-1">{title}</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        <div className="px-7 py-6">
          {body}
          <div className="flex justify-end gap-2 pt-5 mt-5 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-outline-gold min-h-[44px] px-5 py-2.5">Cancel</button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={confirmDisabled}
              data-testid="confirm-clean-test-data"
              className={`min-h-[44px] px-5 py-2.5 uppercase tracking-[0.18em] text-xs ${
                confirmDanger
                  ? "border border-red-400/40 text-red-400 hover:bg-red-400/10"
                  : "btn-gold"
              }`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      {children}
    </div>
  );
}


// ---------- Admin Reset-Password Modal ----------
function AdminResetPasswordModal({ target, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setSubmitting(true);
    try {
      await api.post(`/auth/users/${target.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${target.email}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="admin-reset-pw-modal">
      <div className="bg-[#0F0F0F] border border-[#D4AF37]/20 w-full max-w-md fade-up">
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-start">
          <div>
            <div className="overline text-[#D4AF37]">Admin Override</div>
            <h3 className="serif text-xl mt-1">Reset password</h3>
            <div className="text-xs text-[#8A8A8A] mono mt-2">{target.email}</div>
          </div>
          <button onClick={onClose} data-testid="admin-reset-pw-close">
            <X className="w-5 h-5 text-[#8A8A8A]" />
          </button>
        </div>
        <form onSubmit={submit} className="px-7 py-6 space-y-4">
          <div className="border border-[#C99356]/30 bg-[#C99356]/[0.05] px-3 py-2 text-[0.7rem] text-[#C99356] leading-relaxed">
            You are setting a new password directly. Share it with the user out-of-band
            (Slack / WhatsApp). They should change it after first login.
          </div>
          <Field label="New password">
            <PasswordInput
              required
              minLength={6}
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="luxe-input w-full px-4 py-2.5 text-sm mono"
              data-testid="admin-reset-pw-input"
              placeholder="At least 6 characters"
            />
          </Field>
          <Field label="Confirm new password">
            <PasswordInput
              required
              minLength={6}
              autoComplete="new-password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="luxe-input w-full px-4 py-2.5 text-sm mono"
              data-testid="admin-reset-pw-confirm"
            />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-gold px-5 py-2.5"
              data-testid="admin-reset-pw-submit"
            >
              {submitting ? "Saving…" : "Reset password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ---------- Warranty Card Terms Editor ----------
// Super Admin edits the paragraph that appears on every gemstone warranty
// card PDF. Uses `system_settings.warranty_card.terms` server-side.
function WarrantyCardTermsCard() {
  const [terms, setTerms] = useState("");
  const [defaultTerms, setDefaultTerms] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/warranty-card/settings");
        setTerms(data.terms);
        setDefaultTerms(data.default_terms);
      } catch (e) {
        console.error("Failed to load warranty settings:", e);
      }
    })();
  }, []);

  const save = async () => {
    if (terms.trim().length < 10) {
      return toast.error("Warranty text must be at least 10 characters");
    }
    setSaving(true);
    try {
      const { data } = await api.put("/warranty-card/settings", { terms });
      setTerms(data.terms);
      toast.success("Warranty text updated — reflects on next PDF download");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass-card p-6 sm:p-7 border border-[#D4AF37]/30 mb-8" data-testid="warranty-card-terms-card">
      <div className="flex items-start gap-4">
        <div className="p-3 border border-[#D4AF37]/40 bg-[#D4AF37]/[0.05]">
          <ShieldAlert className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.25} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline text-[#D4AF37]">Gemstone Warranty Card</div>
          <h3 className="serif text-xl mt-1">Warranty & Authenticity paragraph</h3>
          <p className="text-xs text-[#8A8A8A] mt-2 mb-4 leading-relaxed">
            This text is printed on every gemstone warranty card PDF (A5 landscape · one per stone).
            Keep it under 800 characters — long paragraphs will overflow the footer area.
          </p>
          <textarea
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            rows={4}
            maxLength={800}
            className="luxe-input w-full px-4 py-3 text-sm leading-relaxed font-mono"
            data-testid="warranty-terms-textarea"
            placeholder={defaultTerms}
          />
          <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
            <div className="text-[0.65rem] mono text-[#666]">
              {terms.length}/800 chars
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setTerms(defaultTerms)}
                className="text-[0.65rem] uppercase tracking-wider text-[#8A8A8A] hover:text-[#D4AF37] px-3 py-2"
                data-testid="warranty-terms-reset"
                title="Reset to factory default text"
              >
                Reset to default
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="btn-gold px-5 py-2 text-xs uppercase tracking-wider"
                data-testid="warranty-terms-save"
              >
                {saving ? "Saving…" : "Save Warranty Text"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------- Memo Warehouse Whitelist Card ----------
// Super Admin picks which warehouses can source memos. Historically
// SHOWROOM-only; opening it up lets scoped admins (e.g. an ONLINE_SALE
// admin) create + return memos for their own stock.
function MemoWarehouseCard() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/memos/settings");
        setSettings(data);
      } catch (e) {
        console.error("Failed to load memo settings:", e);
      }
    })();
  }, []);

  const toggle = (wh) => {
    setSettings((s) => {
      const set = new Set(s.allowed_warehouses);
      if (set.has(wh)) set.delete(wh);
      else set.add(wh);
      // Preserve canonical order — makes the "currently: X, Y" label stable
      const ordered = s.all_warehouses.filter((w) => set.has(w));
      return { ...s, allowed_warehouses: ordered };
    });
  };

  const save = async () => {
    if (!settings.allowed_warehouses.length) {
      return toast.error("Pick at least one warehouse — memos need a source stock location");
    }
    setSaving(true);
    try {
      const { data } = await api.put("/memos/settings", {
        allowed_warehouses: settings.allowed_warehouses,
      });
      setSettings(data);
      toast.success(`Memo warehouses updated: ${data.allowed_warehouses.join(", ")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;
  const labels = {
    ONLINE_SALE: "Online Sale Inventory",
    SHOWROOM: "Showroom Display",
    LIVE_PRODUCT: "Live Product Stock",
  };

  return (
    <div className="glass-card p-6 sm:p-7 border border-[#7BA585]/30 mb-8" data-testid="memo-warehouse-card">
      <div className="flex items-start gap-4">
        <div className="p-3 border border-[#7BA585]/40 bg-[#7BA585]/[0.05]">
          <FileText className="w-5 h-5 text-[#7BA585]" strokeWidth={1.25} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline text-[#7BA585]">Memo · Stone-on-Consignment</div>
          <h3 className="serif text-xl mt-1">Which warehouses can send memos?</h3>
          <p className="text-xs text-[#8A8A8A] mt-2 mb-4 leading-relaxed">
            Admins (including warehouse-scoped ones) can create <span className="text-[#7BA585]">new memos</span> and record <span className="text-[#7BA585]">returns</span>{" "}
            for any warehouse you enable here. Historically the module was SHOWROOM-only; enable ONLINE_SALE or LIVE_PRODUCT to let
            those warehouse admins issue memos too.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {settings.all_warehouses.map((wh) => {
              const on = settings.allowed_warehouses.includes(wh);
              return (
                <button
                  key={wh}
                  type="button"
                  onClick={() => toggle(wh)}
                  className={`px-4 py-3 text-sm border transition-colors text-left ${
                    on
                      ? "border-[#7BA585]/60 bg-[#7BA585]/10 text-[#EAEAEA]"
                      : "border-white/10 bg-black/20 text-[#8A8A8A] hover:border-white/20"
                  }`}
                  data-testid={`memo-wh-toggle-${wh}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="serif">{labels[wh] || wh}</span>
                    <span className={`text-[0.6rem] mono uppercase tracking-wider ${on ? "text-[#7BA585]" : "text-[#666]"}`}>
                      {on ? "enabled" : "off"}
                    </span>
                  </div>
                  <div className="text-[0.65rem] mono text-[#666] mt-1">{wh}</div>
                </button>
              );
            })}
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="btn-gold px-5 py-2 text-xs uppercase tracking-wider"
            data-testid="memo-warehouse-save"
          >
            {saving ? "Saving…" : "Save Whitelist"}
          </button>
          <span className="text-[0.65rem] mono text-[#7a7a7a] ml-3">
            currently: {settings.allowed_warehouses.join(", ") || "—"}
          </span>
        </div>
      </div>
    </div>
  );
}


// ---------- e-Meterai Threshold Card ----------
function EmeteraiThresholdCard() {
  const [settings, setSettings] = useState(null);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/emeterai");
        setSettings(data);
        setValue(String(data.threshold_idr));
      } catch (e) {
        // Settings card just won't render — log so the cause is visible in DevTools
        console.error("Failed to load e-Meterai settings:", e);
      }
    })();
  }, []);

  const save = async () => {
    const v = parseInt(value, 10);
    if (Number.isNaN(v) || v < 0) return toast.error("Enter a non-negative integer (IDR)");
    setSaving(true);
    try {
      const { data } = await api.put("/settings/emeterai", { threshold_idr: v });
      setSettings((s) => ({ ...s, threshold_idr: data.threshold_idr }));
      toast.success(`Threshold set to Rp ${data.threshold_idr.toLocaleString("id-ID")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update threshold");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  return (
    <div className="glass-card p-6 sm:p-7 border border-[#C99356]/30 mb-8" data-testid="emeterai-threshold-card">
      <div className="flex items-start gap-4">
        <div className="p-3 border border-[#C99356]/40 bg-[#C99356]/[0.05]">
          <Stamp className="w-5 h-5 text-[#C99356]" strokeWidth={1.25} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline text-[#C99356]">e-Meterai · Indonesian stamp duty</div>
          <h3 className="serif text-xl mt-1">Auto-stamp threshold</h3>
          <p className="text-xs text-[#8A8A8A] mt-2 mb-4">
            Documents (memos + invoices) at or above this value will require an
            e-Meterai (Rp 10.000) after the customer signs. Default per UU 10/2020:{" "}
            <span className="mono text-[#D4AF37]">Rp {settings.default_threshold_idr.toLocaleString("id-ID")}</span>.
            Mode: <span className="mono text-[#7BA585]">{settings.mode}</span>.
          </p>
          <div className="flex items-center gap-3">
            <div className="flex items-center border border-[#D4AF37]/30 bg-[#0A0A0A]">
              <span className="px-3 py-2 text-xs text-[#8A8A8A] border-r border-[#D4AF37]/20">Rp</span>
              <input
                type="number"
                step="100000"
                min="0"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="luxe-input px-3 py-2 text-sm mono w-44 border-0"
                data-testid="emeterai-threshold-input"
              />
            </div>
            <button
              onClick={save}
              disabled={saving}
              className="btn-gold px-5 py-2 text-xs uppercase tracking-wider"
              data-testid="emeterai-threshold-save"
            >
              {saving ? "Saving…" : "Save Threshold"}
            </button>
            <span className="text-[0.65rem] mono text-[#7a7a7a]">
              currently Rp {settings.threshold_idr.toLocaleString("id-ID")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------- Company Info Card ----------
// Admin-editable letterhead used on every PDF (invoice, memo, slip gaji,
// supplier statement). Changes apply to the very next PDF generated — no
// redeploy needed.
function CompanyInfoCard() {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/_settings/company-info");
      setData(data);
      setName(data.company_name || "");
      setTagline(data.company_tagline || "");
      setAddress(data.company_address || "");
      setPhone(data.company_phone || "");
      setEmail(data.company_email || "");
    } catch (e) {
      console.error("Failed to load company info:", e);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/_settings/company-info", {
        company_name: name, company_tagline: tagline,
        company_address: address, company_phone: phone, company_email: email,
      });
      toast.success("Info perusahaan disimpan");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;

  return (
    <div className="glass-card p-6 sm:p-7 border border-[#D4AF37]/30 mb-8" data-testid="company-info-card">
      <div className="flex items-start gap-4">
        <div className="p-3 border border-[#D4AF37]/40 bg-[#D4AF37]/[0.05]">
          <Building2 className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.25} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline text-[#D4AF37]">Company Info</div>
          <h3 className="serif text-xl mt-1">Letterhead on every PDF</h3>
          <p className="text-xs text-[#8A8A8A] mt-2 mb-5 leading-relaxed">
            Address, phone, and email here render automatically on every Invoice, Memo, Slip Gaji,
            and Supplier Statement. Updates apply to the next PDF generated — no redeploy needed.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <div className="overline mb-2">Company Name</div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="luxe-input w-full px-3 py-2 text-sm"
                placeholder="Opik Jewels"
                data-testid="company-name-input"
              />
            </label>
            <label className="block">
              <div className="overline mb-2">Tagline</div>
              <input
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                className="luxe-input w-full px-3 py-2 text-sm"
                placeholder="Premium gemstones & jewelry"
                data-testid="company-tagline-input"
              />
            </label>
          </div>
          <label className="block mt-4">
            <div className="overline mb-2">Full Address (multi-line)</div>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={5}
              className="luxe-input w-full px-3 py-2 text-sm font-mono leading-relaxed"
              placeholder="Jakarta Gems Center\nLantai Dasar (Pintu Bacan)\nBlok BKS No. 048-049-050-051\nPasar Rawabening, Jakarta Timur 13320\nIndonesia"
              data-testid="company-address-input"
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <label className="block">
              <div className="overline mb-2">Phone (optional)</div>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="luxe-input w-full px-3 py-2 text-sm mono"
                placeholder="+62 812 3456 7890"
                data-testid="company-phone-input"
              />
            </label>
            <label className="block">
              <div className="overline mb-2">Email (optional)</div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="luxe-input w-full px-3 py-2 text-sm mono"
                placeholder="hello@opikjewels.com"
                data-testid="company-email-input"
              />
            </label>
          </div>
          <div className="flex justify-end mt-5">
            <button
              onClick={save}
              disabled={saving}
              className="btn-gold px-5 py-2 text-xs uppercase tracking-wider"
              data-testid="company-info-save"
            >
              {saving ? "Saving…" : "Save Company Info"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



// ---------- Company Logo Card ----------
// ---------- Brand Asset Card (logo / owner signature / store stamp) ----------
// Admin uploads PNG / JPG / WEBP (≤ 2 MB) that replaces the bundled brand
// asset on every Invoice, Memo, Slip Gaji, Commission Statement, and signed
// PDF. Backed by /_settings/brand-asset/{kind}.
const LOGO_API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const BRAND_ASSET_PRESETS = {
  logo: {
    title: "Company Logo",
    heading: "Brand mark on every PDF",
    description:
      "PNG / JPG / WEBP, max 2 MB. Drop a transparent PNG for the cleanest placement on the dark letterhead. The new logo appears on the very next Invoice, Memo, Slip Gaji, and Commission Statement — no redeploy required.",
    bundledPreviewSrc: "/brand/opikjewels-logo-v2.png",
    bundledLabel: "Bundled fallback",
    icon: ImageIcon,
    previewSize: { width: 220, height: 130 },
    previewBg: "bg-[#0A0A0A]",
  },
  signature: {
    title: "Owner Signature",
    heading: "Tertanda · Pemilik Toko",
    description:
      "Optional. Upload a transparent PNG of your handwritten signature (sign on plain white paper, snap a photo, then remove the background at remove.bg). It appears in the bottom-right of every Invoice and Memo so they're ready to send without a wet sign.",
    bundledPreviewSrc: "",
    bundledLabel: "Not set — leaves a blank signing line",
    icon: PenLine,
    previewSize: { width: 220, height: 100 },
    previewBg: "bg-[#FAFAFA]",
  },
  stamp: {
    title: "Store Stamp · Stempel Toko",
    heading: "Digital seal on every Invoice & Memo",
    description:
      "Optional. Upload a transparent PNG of your toko's round/square stamp (the same one you'd press on a paper receipt). It overlaps the signature like a wet stamp — looks officially Indonesian.",
    bundledPreviewSrc: "",
    bundledLabel: "Not set — no stamp will be printed",
    icon: Stamp,
    previewSize: { width: 130, height: 130 },
    previewBg: "bg-[#FAFAFA]",
  },
};

function BrandAssetCard({ kind }) {
  const preset = BRAND_ASSET_PRESETS[kind];
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [cacheBust, setCacheBust] = useState(Date.now());
  const Icon = preset.icon;

  const load = async () => {
    try {
      const { data } = await api.get(`/_settings/brand-asset/${kind}`);
      setInfo(data);
      setCacheBust(Date.now());
    } catch (e) {
      console.error(`Failed to load brand asset ${kind}:`, e);
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(`${preset.title} too large (max 2 MB)`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/_settings/brand-asset/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${preset.title} updated — appears on next PDF generated`);
      setInfo({ ...data, using_bundled_fallback: false, has_bundled_fallback: preset.bundledPreviewSrc !== "" });
      setCacheBust(Date.now());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const revert = async () => {
    const fallbackMsg = preset.bundledPreviewSrc
      ? `Revert to the bundled ${preset.title.toLowerCase()}? Your uploaded file stays in storage but won't be used on new PDFs.`
      : `Remove your custom ${preset.title.toLowerCase()}? PDFs won't include this asset until you upload a new one.`;
    if (!window.confirm(fallbackMsg)) return;
    setBusy(true);
    try {
      await api.delete(`/_settings/brand-asset/${kind}`);
      toast.success(preset.bundledPreviewSrc ? "Reverted to bundled" : "Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revert failed");
    } finally {
      setBusy(false);
    }
  };

  if (!info) return null;

  const uploadedSrc = info.path
    ? `${LOGO_API_BASE}/api/files/${info.path}?v=${cacheBust}`
    : "";

  return (
    <div className="glass-card p-6 sm:p-7 border border-[#D4AF37]/30 mb-8" data-testid={`brand-asset-${kind}-card`}>
      <div className="flex items-start gap-4">
        <div className="p-3 border border-[#D4AF37]/40 bg-[#D4AF37]/[0.05]">
          <Icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.25} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline text-[#D4AF37]">{preset.title}</div>
          <h3 className="serif text-xl mt-1">{preset.heading}</h3>
          <p className="text-xs text-[#8A8A8A] mt-2 mb-5 leading-relaxed">
            {preset.description}
          </p>

          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <div
              className={`border border-white/10 ${preset.previewBg} p-4 flex items-center justify-center`}
              style={preset.previewSize}
            >
              {info.path ? (
                <img
                  src={uploadedSrc}
                  alt={`Current ${preset.title}`}
                  className="max-w-full max-h-full object-contain"
                  data-testid={`brand-asset-${kind}-preview`}
                />
              ) : preset.bundledPreviewSrc ? (
                <img
                  src={preset.bundledPreviewSrc}
                  alt={`Bundled ${preset.title}`}
                  className="max-w-full max-h-full object-contain opacity-90"
                  data-testid={`brand-asset-${kind}-bundled-preview`}
                />
              ) : (
                <div className="text-center text-[0.6rem] mono uppercase tracking-wider text-[#8A8A8A]">
                  {preset.bundledLabel}
                </div>
              )}
            </div>

            <div className="flex-1 space-y-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => upload(e.target.files?.[0])}
                data-testid={`brand-asset-${kind}-input`}
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="btn-gold px-5 py-2 text-xs uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50"
                data-testid={`brand-asset-${kind}-upload`}
              >
                <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                {busy ? "Uploading…" : info.path ? `Replace ${preset.title}` : `Upload ${preset.title}`}
              </button>
              {info.path && (
                <div>
                  <button
                    onClick={revert}
                    disabled={busy}
                    className="btn-outline-gold px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid={`brand-asset-${kind}-revert`}
                  >
                    <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {preset.bundledPreviewSrc ? "Revert to bundled" : "Remove"}
                  </button>
                </div>
              )}
              <div className="text-[0.65rem] mono uppercase tracking-wider text-[#666]">
                {info.path
                  ? `Using uploaded ${preset.title.toLowerCase()}`
                  : preset.bundledPreviewSrc
                  ? `Using bundled ${preset.title.toLowerCase()}`
                  : `No ${preset.title.toLowerCase()} set`}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Wrapper that renders the 3 brand asset cards (logo / signature / stamp).
function BrandAssetsSection() {
  return (
    <>
      <BrandAssetCard kind="logo" />
      <BrandAssetCard kind="signature" />
      <BrandAssetCard kind="stamp" />
    </>
  );
}

// Back-compat alias used by the parent <SuperAdmin> tree. Keeps the old
// component name working so we don't have to thread changes through every
// snapshot/test that may reference CompanyLogoCard.
function CompanyLogoCard() {
  return <BrandAssetsSection />;
}
